/**
 * react-select appearance for the coupon form, matching `inputClass` in FormField.js.
 *
 * Done through the `styles` prop rather than CSS. react-select composes its own emotion classes at
 * runtime, so overriding them from a stylesheet means guessing at generated class names and
 * out-specifying whatever they resolve to; the `styles` prop is the library's supported hook and
 * merges over the defaults deterministically.
 *
 * `menuPortalTarget` is set on the components themselves — the form body scrolls
 * (`overflow-y: auto`), which clips an absolutely-positioned menu opened near the bottom. Portalling
 * to document.body escapes that, and because the appearance lives in these objects rather than in
 * `.coupon-codes-page` CSS, a menu rendered outside the page scope still looks right.
 */

const NEUTRAL_200 = '#e5e5e5';
const NEUTRAL_400 = '#a3a3a3';
const NEUTRAL_500 = '#737373';
const NEUTRAL_900 = '#171717';
const BRAND_50 = '#fdf5f3';
const BRAND_500 = '#b24230';
const BRAND_700 = '#7b1a10';

// Bootstrap's modal is z-index 1055; the menu has to clear it once portalled to document.body.
const MENU_Z_INDEX = 1100;

const selectStyles = {
    // `minHeight`, not `height`: an isMulti control has to grow as chips are added.
    // Width and style are stated outright rather than inherited from `base`, because the page's
    // own `:where(.coupon-codes-page) *` reset zeroes border-width for everything inside it.
    control: (base, state) => ({
        ...base,
        minHeight: '2.75rem',
        borderRadius: '0.75rem',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: state.isFocused ? BRAND_500 : NEUTRAL_200,
        backgroundColor: state.isDisabled ? '#fafafa' : '#fff',
        boxShadow: state.isFocused ? `0 0 0 4px rgba(178, 66, 48, 0.1)` : 'none',
        fontSize: '0.875rem',
        transition: 'border-color 0.15s, box-shadow 0.15s',
        ':hover': { borderColor: state.isFocused ? BRAND_500 : NEUTRAL_200 },
    }),
    valueContainer: base => ({ ...base, padding: '0.25rem 0.75rem', gap: '0.25rem' }),
    placeholder: base => ({ ...base, color: NEUTRAL_400, margin: 0 }),
    singleValue: base => ({ ...base, color: NEUTRAL_900, margin: 0 }),
    input: base => ({ ...base, color: NEUTRAL_900, margin: 0, padding: 0 }),

    // The default separator reads as a seam in a rounded, borderless-looking control.
    indicatorSeparator: () => ({ display: 'none' }),
    dropdownIndicator: base => ({
        ...base,
        color: NEUTRAL_400,
        padding: '0 0.75rem 0 0',
        ':hover': { color: NEUTRAL_500 },
    }),
    clearIndicator: base => ({
        ...base,
        color: NEUTRAL_400,
        padding: '0 0.25rem',
        ':hover': { color: NEUTRAL_500 },
    }),

    multiValue: base => ({
        ...base,
        backgroundColor: BRAND_50,
        borderRadius: '0.375rem',
        margin: 0,
    }),
    multiValueLabel: base => ({
        ...base,
        color: BRAND_700,
        fontSize: '0.75rem',
        fontWeight: 600,
        padding: '0.125rem 0.25rem 0.125rem 0.5rem',
    }),
    multiValueRemove: base => ({
        ...base,
        color: BRAND_700,
        borderRadius: '0 0.375rem 0.375rem 0',
        ':hover': { backgroundColor: '#fae7e3', color: BRAND_700 },
    }),

    menu: base => ({
        ...base,
        borderRadius: '0.75rem',
        border: `1px solid ${NEUTRAL_200}`,
        boxShadow: '0 24px 48px -12px rgba(16, 24, 40, 0.24)',
        overflow: 'hidden',
        fontSize: '0.875rem',
        zIndex: MENU_Z_INDEX,
    }),
    menuPortal: base => ({ ...base, zIndex: MENU_Z_INDEX }),
    menuList: base => ({ ...base, padding: '0.25rem' }),
    option: (base, state) => ({
        ...base,
        borderRadius: '0.5rem',
        padding: '0.5rem 0.75rem',
        cursor: 'pointer',
        color: state.isSelected ? '#fff' : '#404040',
        backgroundColor: state.isSelected
            ? BRAND_700
            : state.isFocused
            ? BRAND_50
            : 'transparent',
        ':active': { backgroundColor: state.isSelected ? BRAND_700 : BRAND_50 },
    }),
    noOptionsMessage: base => ({ ...base, fontSize: '0.875rem', color: NEUTRAL_400 }),
};

export default selectStyles;
